// src/services/api/wallet.service.ts
import { authService } from '../auth.service';
import { DepositDetails } from '../../types/wallet.types'
import { getServerUrl, getCurrentApiUrl } from '../../config/server.config';
interface WalletDetails {
  address: string;
  balance: string;
  usdtBalance?: string;
}

interface UserWallets {
  ethereum: Array<{
    id: string;
    userId: string;
    address: string;
    createdAt: string;
    balances?: {
      eth?: string;
      usdt?: string;
      usdc?: string;
    };
  }>;
  bitcoin: Array<{
    id: string;
    userId: string;
    address: string;
    createdAt: string;
    balances?: {
      btc?: string;
    };
  }>;
  tron: Array<{
    id: string;
    userId: string;
    address: string;
    createdAt: string;
    balances?: {
      trx?: string;
      usdt?: string;
    };
  }>;
  bnb: Array<{
    id: string;
    userId: string;
    address: string;
    createdAt: string;
    balances?: {
      bnb?: string;
      usdt?: string;
    };
  }>;
  solana?: Array<{
    id: string;
    userId: string;
    address: string;
    createdAt: string;
    balances?: {
      sol?: string;
      usdt?: string;
      usdc?: string;
    };
  }>;
}
interface importReturndData {
  error:string,
  wallets: Array<{
    currency: string;
    address: string;
    balance: string;
  }>;
}
interface DashboardData {
  wallets: Array<{
    currency: string;
    address: string;
    balance: string;
  }>;
}

export class WalletService {
  // Hacer baseUrl dinámico con un getter
  private get baseUrl(): string {
    return getServerUrl(true); // Siempre obtiene la URL actual del switch
  }
  private requestQueue: Record<string, Promise<any>> = {}; // Para evitar peticiones duplicadas
  private requestTimeouts: Record<string, number> = {
    ethereum: 15000, // 15 segundos para Ethereum
    bitcoin: 10000,  // 10 segundos para Bitcoin
    tron: 20000,     // 20 segundos para Tron (más tiempo debido a problemas frecuentes)
    bnb: 15000,      // 15 segundos para BNB
    default: 8000    // 8 segundos por defecto
  };

  // Función auxiliar para obtener el timeout adecuado para cada tipo de endpoint
  private getTimeoutForEndpoint(endpoint: string): number {
    if (endpoint.includes('/ethereum/') || endpoint.includes('/usdt/')) {
      return this.requestTimeouts.ethereum;
    } else if (endpoint.includes('/bitcoin/')) {
      return this.requestTimeouts.bitcoin;
    } else if (endpoint.includes('/tron/')) {
      return this.requestTimeouts.tron;
    } else if (endpoint.includes('/bnb/')) {
      return this.requestTimeouts.bnb;
    }
    return this.requestTimeouts.default;
  }

  // Función mejorada para manejar peticiones con retry y backoff exponencial
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retries = 2,
    backoffFactor = 1.5
  ): Promise<T> {
    // Crear una clave única para este endpoint
    const cacheKey = `${options.method || 'GET'}-${endpoint}`;

    // Verificar si la solicitud ya está en proceso
    if (this.requestQueue[cacheKey] !== undefined) {
      try {
        return await this.requestQueue[cacheKey];
      } catch (error) {
        // Si falla, eliminar de la cola para permitir reintentarlo
        delete this.requestQueue[cacheKey];
        throw error;
      }
    }

    // Implementación de backoff exponencial
    const fetchWithRetry = async (retryCount: number, delay: number): Promise<T> => {
      try {
        const timeout = this.getTimeoutForEndpoint(endpoint);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(`${this.baseUrl}${endpoint}`, {
          ...options,
          signal: controller.signal,
          headers: {
            'Accept': '*/*',
            'Content-Type': 'application/json',
            'Authorization': authService.getAuthorizationHeader(),
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            ...options.headers,
          },
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // Si es un error 401, puede que el token haya expirado
          if (response.status === 401) {
            console.warn('Authentication error, token might have expired');
            // Aquí podrías implementar una renovación de token si es necesario
          }

          const errorData = await response.json().catch(() => ({ message: `HTTP error ${response.status}` }));
          throw new Error(errorData.message || `Request failed: ${response.statusText} - ${response.status}`);
        }

        return await response.json();
      } catch (error: unknown) {
        const err = error as Error;

        if (retryCount <= 0) {
          throw err; // Sin más reintentos
        }

        // Determinar si debemos reintentar
        const shouldRetry = !(
          // No reintentar para errores de autenticación (401)
          (err.message && err.message.includes('401')) ||
          // No reintentar para ciertos tipos de errores como malformación JSON
          (err.message && err.message.includes('JSON'))
        );

        if (shouldRetry) {
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchWithRetry(retryCount - 1, delay * backoffFactor);
        }

        throw err;
      }
    };

    // Almacenar la promesa en la cola
    this.requestQueue[cacheKey] = fetchWithRetry(retries, 1000) // Empezar con 1s de delay
      .finally(() => {
        // Eliminar de la cola una vez completada
        delete this.requestQueue[cacheKey];
      });

    return this.requestQueue[cacheKey] as Promise<T>;
  }

  // Métodos para trabajar con caché
  private getCachedData<T>(key: string, validityInMs = 180000): { data: T | null, isValid: boolean } {
    const cachedData = localStorage.getItem(key);
    const cachedTimestamp = localStorage.getItem(`${key}-timestamp`);

    if (!cachedData || !cachedTimestamp) {
      return { data: null, isValid: false };
    }

    const cacheAge = Date.now() - parseInt(cachedTimestamp);
    const isValid = cacheAge < validityInMs;

    return {
      data: JSON.parse(cachedData) as T,
      isValid
    };
  }

  private setCachedData<T>(key: string, data: T): void {
    localStorage.setItem(key, JSON.stringify(data));
    localStorage.setItem(`${key}-timestamp`, Date.now().toString());
  }

  // Obtener las wallets del usuario desde el backend
  async getUserWallets(): Promise<UserWallets> {
    try {
      // TEMPORAL: Desactivar caché para debug USDT BEP-20
      // const { data: cachedWallets, isValid: cacheValid } = this.getCachedData<UserWallets>('userWallets', 120000);
      // if (cachedWallets && cacheValid) {
      //   return cachedWallets;
      // }

      const wallets = await this.makeRequest<UserWallets>('/crypto-web3/wallets/user-wallets');
      return wallets;
    } catch (error) {
      console.error('❌ Error in getUserWallets:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown',
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });

      // NO usar caché antiguo porque puede tener datos incorrectos
      // El problema de USDT BEP-20 estaba aquí - devolvía caché con valores viejos

      return { ethereum: [], bitcoin: [], tron: [], bnb: [], solana: [] };
    }
  }

  // Procesar una única wallet de Bitcoin
  private async processBitcoinWallet(wallet: any) {
    try {
      const endpoint = `/bitcoin/wallet/${wallet.address}`;

      // Intentar obtener datos de caché
      const cacheKey = `btc-wallet-${wallet.address}`;
      const { data: cachedData, isValid: cacheValid } = this.getCachedData<any>(cacheKey, 180000);

      if (cachedData && cacheValid) {
        return cachedData;
      }

      const walletDetails = await this.makeRequest<any>(endpoint);

      const balance = walletDetails && walletDetails.balance ? String(walletDetails.balance) : '0';

      const walletData = {
        currency: 'BTC',
        address: wallet.address,
        balance
      };

      return walletData;
    } catch (error) {

      // Intentar usar caché antigua en caso de error
      const { data: cachedData } = this.getCachedData<any>(`btc-wallet-${wallet.address}`);
      if (cachedData) {
        return cachedData;
      }

      // Si no hay caché, devolver objeto con balance 0
      return {
        currency: 'BTC',
        address: wallet.address,
        balance: '0'
      };
    }
  }

  // Procesar una única wallet de Ethereum (incluye USDT_ERC20)
  private async processEthereumWallet(wallet: any) {
    try {
      const results = [];

      // Para Ethereum
      try {
        const ethEndpoint = `/ethereum/wallet/${wallet.address}`;

        // Intentar obtener datos de caché
        const cacheKey = `eth-wallet-${wallet.address}`;
        const { data: cachedData, isValid: cacheValid } = this.getCachedData<any>(cacheKey, 180000);

        // if (cachedData && cacheValid) {
        //   // results.push(cachedData);
        // } else {
        const ethWalletDetails = await this.makeRequest<any>(ethEndpoint);

        const ethBalance = ethWalletDetails && ethWalletDetails.balance ? String(ethWalletDetails.balance) : '0';

        const ethData = {
          currency: 'ETH',
          address: wallet.address,
          balance: ethBalance
        };
        results.push(ethData);
        // }
      } catch (ethError) {

        // Intentar usar caché antigua en caso de error
        const { data: cachedData } = this.getCachedData<any>(`eth-wallet-${wallet.address}`);
        if (cachedData) {
          results.push(cachedData);
        } else {
          // Si no hay caché, añadir objeto con balance 0
          results.push({
            currency: 'ETH',
            address: wallet.address,
            balance: '0'
          });
        }
      }

      // Esperar un poco antes de la siguiente petición para evitar saturar el servidor
      await new Promise(resolve => setTimeout(resolve, 500));

      // Para USDT en la misma wallet de Ethereum
      try {
        const usdtEndpoint = `/usdt/wallet/${wallet.address}/balance`;

        // Intentar obtener datos de caché
        const cacheKey = `usdt-erc20-wallet-${wallet.address}`;
        const { data: cachedData, isValid: cacheValid } = this.getCachedData<any>(cacheKey, 180000);

        if (cachedData && cacheValid) {
          results.push(cachedData);
        } else {
          const usdtWalletDetails = await this.makeRequest<any>(usdtEndpoint);

          const usdtBalance = usdtWalletDetails && usdtWalletDetails.balance ? String(usdtWalletDetails.balance) : '0';

          const usdtData = {
            currency: 'USDT_ERC20',
            address: wallet.address,
            balance: usdtBalance
          };
          results.push(usdtData);
        }
      } catch (usdtError) {

        // Intentar usar caché antigua en caso de error
        const { data: cachedData } = this.getCachedData<any>(`usdt-erc20-wallet-${wallet.address}`);
        if (cachedData) {
          results.push(cachedData);
        } else {
          // Si no hay caché, añadir objeto con balance 0
          results.push({
            currency: 'USDT_ERC20',
            address: wallet.address,
            balance: '0'
          });
        }
      }

      return results;
    } catch (error) {

      // En caso de error, devolver ambos objetos con balance 0
      return [
        {
          currency: 'ETH',
          address: wallet.address,
          balance: '0'
        },
        {
          currency: 'USDT_ERC20',
          address: wallet.address,
          balance: '0'
        }
      ];
    }
  }

  // Procesar una única wallet de Tron - Mejorado para mayor robustez
  private async processTronWallet(wallet: any) {
    try {
      const endpoint = `/tron/wallet/${wallet.address}`;

      // Intentar obtener datos de caché
      const cacheKey = `tron-wallet-${wallet.address}`;
      const { data: cachedData, isValid: cacheValid } = this.getCachedData<any>(cacheKey, 300000);

      // Si hay caché válida, usarla
      // if (cachedData) {
      //   return {
      //     currency: 'TRX',  // Cambiar a TRX
      //     address: wallet.address,
      //     balance: cachedData.trxBalance || cachedData.balance || '0',
      //     trxBalance: cachedData.trxBalance || '0',
      //     usdtBalance: cachedData.usdtBalance || '0',
      //     tronscanUrl: cachedData.tronscanUrl || `https://tronscan.org/#/address/${wallet.address}`
      //   };
      // }

      try {
        // Usamos un timeout más largo para Tron
        const walletDetails = await this.makeRequest<any>(endpoint, {}, 3);

        // Priorizar TRX balance, luego USDT, y finalmente el balance general
        const trxBalance = walletDetails.trxBalance || '0';
        const usdtBalance = walletDetails.usdtBalance || '0';

        const walletData = {
          currency: 'TRX',
          address: wallet.address,
          balance: walletDetails.trxBalance || '0',  // Usar trxBalance como balance principal
          trxBalance: walletDetails.trxBalance || '0',
          usdtBalance: walletDetails.usdtBalance || '0',
          tronscanUrl: `https://tronscan.org/#/address/${wallet.address}`
        };
        return walletData;
      } catch (fetchError) {

        // Objeto por defecto con balance 0
        const walletData = {
          currency: 'TRX',  // Cambiar a TRX
          address: wallet.address,
          balance: '0',
          trxBalance: '0',
          usdtBalance: '0',
          tronscanUrl: `https://tronscan.org/#/address/${wallet.address}`
        };

        this.setCachedData(cacheKey, walletData);

        return walletData;
      }
    } catch (error) {

      return {
        currency: 'TRX',  // Cambiar a TRX
        address: wallet.address,
        balance: '0',
        trxBalance: '0',
        usdtBalance: '0',
        tronscanUrl: `https://tronscan.org/#/address/${wallet.address}`
      };
    }
  }

  // Procesar una única wallet de BNB
  private async processBNBWallet(wallet: any) {
    const results = [];
    try {
      const endpoint = `/bnb/wallet/${wallet.address}`;

      // Intentar obtener datos de caché
      const cacheKey = `bnb-wallet-${wallet.address}`;
      // const { data: cachedData, isValid: cacheValid } = this.getCachedData<any>(cacheKey, 180000);

      // if (cachedData && cacheValid) {
      //   return cachedData;
      // }

      const walletDetails = await this.makeRequest<any>(endpoint, {}, 2, 2); // 2 reintentos con backoff x2

      const balance = walletDetails && walletDetails.balance ? String(walletDetails.balance) : '0';

      const walletData = {
        currency: 'BNB',
        address: wallet.address,
        balance
      };

      results.push(walletData);

    } catch (error) {

      // Intentar usar caché antigua en caso de error
      const { data: cachedData } = this.getCachedData<any>(`bnb-wallet-${wallet.address}`);
      if (cachedData) {
        return cachedData;
      }
      results.push({
        currency: 'BNB',
        address: wallet.address,
        balance: '0'
      });
      // Si no hay caché, devolver objeto con balance 0
      // return {
      //   currency: 'BNB',
      //   address: wallet.address,
      //   balance: '0'
      // };
    }
    // Esperar un poco antes de la siguiente petición para evitar saturar el servidor
    await new Promise(resolve => setTimeout(resolve, 500));
    // Para USDT en la misma wallet de Ethereum
    try {
      // const confirm = await this.makeRequest<any>("/api/usdt-bep20/token-info");
      const usdtEndpoint = `/usdt-bep20/wallet/${wallet.address}/balance`;

      // Intentar obtener datos de caché
      const cacheKey = `usdt-bep20-wallet-${wallet.address}`;
      // const { data: cachedData, isValid: cacheValid } = this.getCachedData<any>(cacheKey, 180000);

      // if (cachedData && cacheValid) {
      //   results.push(cachedData);
      // } else {
      const usdtWalletDetails = await this.makeRequest<any>(usdtEndpoint);

      const usdtBalance = usdtWalletDetails && usdtWalletDetails.balance ? String(usdtWalletDetails.balance) : '0';

      const walletData = {
        currency: 'USDT_BEP20',
        address: wallet.address,
        balance: usdtBalance
      };

      // Guardar en caché
      this.setCachedData(cacheKey, walletData);

      results.push(walletData);
      // }
    } catch (usdtError) {

      // Intentar usar caché antigua en caso de error
      const { data: cachedData } = this.getCachedData<any>(`usdt-erc20-wallet-${wallet.address}`);
      if (cachedData) {
        results.push(cachedData);
      } else {
        // Si no hay caché, añadir objeto con balance 0
        results.push({
          currency: 'USDT_BEP20',
          address: wallet.address,
          balance: '0'
        });
      }
    }
    return results;
  }

  // Método para procesar wallets en lotes
  private async processBatch<T>(
    items: Array<any>,
    processFn: (item: any) => Promise<T | Array<T>>,
    batchSize = 2,
    delayBetweenItems = 300
  ): Promise<Array<T>> {
    const results: Array<T> = [];

    // Procesar en lotes pequeños para no sobrecargar el servidor
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const batchPromises = batch.map(item => processFn(item));
      // Procesar el lote en paralelo
      const batchResults = await Promise.allSettled(batchPromises);
      // Agregar resultados exitosos
      batchResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const value = result.value;
          if (Array.isArray(value)) {
            results.push(...value);
          } else {
            results.push(value);
          }
        } else {
          // Añadir un placeholder para mantener coherencia
          const originalItem = batch[index];
          results.push({
            currency: originalItem.currency || 'UNKNOWN',
            address: originalItem.address,
            balance: '0'
          } as unknown as T);
        }
      });

      // Esperar antes de procesar el siguiente lote
      if (i + batchSize < items.length) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenItems * batchSize));
      }
    }

    return results;
  }

  // Dashboard Data - Con procesamiento por lotes y paralelización controlada
  async getCryptoWallets(): Promise<any> {
    try {
      const userWallets = await this.getUserWallets();

      if (userWallets.bnb && userWallets.bnb.length > 0) {
        if (userWallets.bnb[0].balances) {
        }
      }

      // El backend ya devuelve los balances, solo necesitamos formatear la respuesta
      return userWallets;
    } catch (error) {
      console.error('Error getting crypto wallets:', error);
      return {
        ethereum: [],
        bitcoin: [],
        tron: [],
        bnb: [],
        solana: []
      };
    }
  }

  // Método para invalidar caché después de crear wallets
  private invalidateCache() {
    localStorage.removeItem('userWallets');
    localStorage.removeItem('dashboardData');
  }

  // Métodos para crear wallets con mejor manejo de errores
  async createEthereumWallet(): Promise<any> {
    try {
      const result = await this.makeRequest<any>('/crypto-web3/ethereum/wallet', {
        method: 'POST'
      }, 2); // 2 reintentos

      // this.invalidateCache();

      return result;
    } catch (error) {
      throw error;
    }
  }

  async createBitcoinWallet(): Promise<any> {
    try {
      const result = await this.makeRequest<any>('/crypto-web3/bitcoin/wallet', {
        method: 'POST'
      }, 2);

      // this.invalidateCache();
      return result;
    } catch (error) {
      console.error('Error creating Bitcoin wallet:', error);
      throw error;
    }
  }

  async createUSDTWallet(): Promise<any> {
    return this.createEthereumWallet();
  }

  // async createTronWallet(): Promise<WalletDetails> {
  //   try {
  //     const result = await this.makeRequest<WalletDetails>('/crypto-web3/tron/wallet', {
  //       method: 'POST'
  //     }, 3); // 3 reintentos para Tron debido a problemas conocidos
  //     this.invalidateCache();

  //     // Añadir un retraso antes de devolver para permitir que el servidor complete la creación
  //     await new Promise(resolve => setTimeout(resolve, 1000));

  //     return result;
  //   } catch (error) {

  //     // Si el error es 401 después de crear, es posible que la wallet se haya creado pero haya problemas
  //     // con la autenticación. Podemos intentar obtener las wallets para verificar
  //     if ((error as Error).message.includes('401')) {
  //       try {
  //         // Invalidar caché forzosamente para obtener datos frescos
  //         this.invalidateCache();

  //         // Esperar un momento y luego verificar las wallets
  //         await new Promise(resolve => setTimeout(resolve, 2000));
  //         const wallets = await this.getUserWallets();

  //         // Si hay wallets Tron, devolver la última
  //         if (wallets.tron && wallets.tron.length > 0) {
  //           const lastWallet = wallets.tron[wallets.tron.length - 1];
  //           return {
  //             address: lastWallet.address,
  //             balance: '0'
  //           };
  //         }
  //       } catch (checkError) {
  //         console.error('Error checking if Tron wallet was created:', checkError);
  //       }
  //     }

  //     throw error;
  //   }
  // }

  async createTrx(): Promise<any> {
    try {
      const result = await this.makeRequest<any>('/crypto-web3/tron/wallet', {
        method: 'POST'
      }, 3); // 3 reintentos para Tron debido a problemas conocidos

      // this.invalidateCache();

      // Añadir un retraso antes de devolver para permitir que el servidor complete la creación
      // await new Promise(resolve => setTimeout(resolve, 1000));

      return result;
    } catch (error) {
      // Manejo de errores similar a createTronWallet
      if ((error as Error).message.includes('401')) {
        try {
          this.invalidateCache();

          await new Promise(resolve => setTimeout(resolve, 2000));
          const wallets = await this.getUserWallets();

          if (wallets.tron && wallets.tron.length > 0) {
            const lastWallet = wallets.tron[wallets.tron.length - 1];
            return {
              wallet: {
                address: lastWallet.address,
                balance: '0'
              },
              bothCreated: true
            };
          }
        } catch (checkError) {
          console.error('Error checking if TRON wallet was created:', checkError);
        }
      }

      throw error;
    }
  }

  async createBNBWallet(): Promise<any> {
    try {
      const result = await this.makeRequest<any>('/crypto-web3/bnb/wallet', {
        method: 'POST'
      }, 2); // 2 reintentos

      // this.invalidateCache();

      return result;
    } catch (error) {
      console.error('Error creating BNB wallet:', error);
      throw error;
    }
  }
   async createSOLWallet(): Promise<any> {
    try {
      const result = await this.makeRequest<any>('/crypto-web3/solana/wallet', {
        method: 'POST'
      }, 2); // 2 reintentos

      // this.invalidateCache();

      return result;
    } catch (error) {
      console.error('Error creating SOL wallet:', error);
      throw error;
    }
  }

  async createbep20PrivateKeyWallet(walletData: any): Promise<any> {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Send POST request with wallet data
      const response = await fetch(`${getCurrentApiUrl()}/restore/usdt-bep20/restore-private-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(walletData),
      });

      // Check if response is OK
      if (!response.ok) {
        throw new Error(`Failed to create wallet: ${response.status} ${response.statusText}`);
      }

      // Parse and return response data
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating BNB wallet:', error);
      throw error;
    }
  }
  async createbep20SeedKeyWallet(walletData: any): Promise<any> {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Send POST request with wallet data
      const response = await fetch(`${getCurrentApiUrl()}/restore/usdt-bep20/restore-mnemonic`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(walletData),
      });

      // Check if response is OK
      if (!response.ok) {
        throw new Error(`Failed to create wallet: ${response.status} ${response.statusText}`);
      }

      // Parse and return response data
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating BNB wallet:', error);
      throw error;
    }
  }
  async createbnbPrivateKeyWallet(walletData: any): Promise<any> {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Send POST request with wallet data
      const response = await fetch(`${getCurrentApiUrl()}/restore/bnb/restore-private-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(walletData),
      });

      // Check if response is OK
      if (!response.ok) {
        throw new Error(`Failed to create wallet: ${response.status} ${response.statusText}`);
      }

      // Parse and return response data
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating BNB wallet:', error);
      throw error;
    }
  }


  // async createWallet(currency: string): Promise<WalletResponse> {

  async createWallet(currency: string) {

    try {
      switch (currency.toLowerCase()) {
        case 'btc': {
          const res = await this.createBitcoinWallet();
          return res;
        }
        // case 'eth': {
        //   const { wallet, bothCreated } = await this.createEthereumWallet();
        //   let message = "We've created both Ethereum (ETH) and USDT (ERC-20) wallets for you since they share the same address. You can use this wallet for both currencies.";
        //   return { wallet, message };
        // }
        case 'eth':
        case 'usdt_erc20': 
        case 'usdc_erc20':{
          const res = await this.createUSDTWallet();
          // let message = "We've created both Ethereum (ETH) and USDT (ERC-20) wallets for you since they share the same address. You can use this wallet for both currencies.";
          return res;
        }
        // case 'usdc_erc20': {
        //   const res = await this.createUSDTWallet();
        //   // let message = "We've created both Ethereum (ETH) and USDT (ERC-20) wallets for you since they share the same address. You can use this wallet for both currencies.";
        //   return res;
        // }
        // case 'trx': {
        //   const {wallet} = await this.createTrx();
        //   let message = "We've created both TRON (TRX) and USDT (TRC20) wallets for you since they share the same address. You can use this wallet for both currencies.";
        //   return { wallet, message };
        // }
        case 'trx':
        case 'usdt_tron': {
          const res = await this.createTrx();
          let message = "We've created both TRON (TRX) and USDT (TRC20) wallets for you since they share the same address. You can use this wallet for both currencies.";
          return res;
        }
        case 'bnb':
        case 'usdt_bep20': {
          const res = await this.createBNBWallet();
          let message = "We've created both BNB and USDT (BEP-20) wallets for you since they share the same address. You can use this wallet for both currencies.";
          return res;
        }
        // case 'usdt_bnb': {
        //   const wallet = await this.createUSDTBNBWallet();
        //   return { wallet };
        // }
        case 'sol':
        case 'usdt_spl': {
          const res = await this.createSOLWallet();
          let message = "We've created both BNB and USDT (BEP-20) wallets for you since they share the same address. You can use this wallet for both currencies.";
          return res;
        }
         case 'usdc_spl': {
          const res = await this.createSOLWallet();
          let message = "We've created both BNB and USDT (BEP-20) wallets for you since they share the same address. You can use this wallet for both currencies.";
          return res;
        }
        default:
          throw new Error(`Unsupported currency: ${currency}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('401')) {
        throw new Error(`Authentication error while creating ${currency} wallet. Please log in again.`);
      } else if (errorMessage.includes('timeout') || errorMessage.includes('aborted')) {
        throw new Error(`Network timeout while creating ${currency} wallet. The server is taking too long to respond. Please try again later.`);
      } else if (errorMessage.includes('500')) {
        throw new Error(`Server error while creating ${currency} wallet. Our team has been notified. Please try again later.`);
      }

      throw error;
    }
  }
  async getQRCode (endpoint:string, address:string) {
     const controller = new AbortController();
     const response = await fetch(`${this.baseUrl}${endpoint}`, {
          headers: {
            'Authorization': authService.getAuthorizationHeader()
          },
          signal: controller.signal
        });
      if (response.ok) {
          const data = await response.json();
          const qrCode = data.qrCode || `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${address}`;    
          return qrCode;            
        } else {
          // Si falla, usar un generador público
          const fallbackQR = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${address}`;
           return fallbackQR;   
          // localStorage.setItem(cacheKey, fallbackQR);
        }
    
  }
  async getPrivateKey (endpoint:string, address:string) {
     const controller = new AbortController();
     const response = await fetch(`${this.baseUrl}${endpoint}`, {
          headers: {
            'Authorization': authService.getAuthorizationHeader()
          },
          signal: controller.signal
        });
      return response;
  }
  async getSeed (endpoint:string, address:string) {
     const controller = new AbortController();
     const response = await fetch(`${this.baseUrl}${endpoint}`, {
          headers: {
            'Authorization': authService.getAuthorizationHeader()
          },
          signal: controller.signal
        });
      return response;
  }
  async getTRXTransactions(address: string): Promise<any[]> {
    try {
      const endpoint = `/tron/wallet/${address}/transactions`;
      const transactions = await this.makeRequest<any[]>(endpoint);
      return transactions.map(tx => ({
        ...tx,
        currency: 'TRX'
      }));
    } catch (error) {
      return [];
    }
  }

  async getWalletDepositDetails(accountId: string): Promise<DepositDetails> {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        return;
      }

      // REMOVED: Rail Operations no longer exists in V2
      // Deposit details should be fetched from Zenus provider for fiat accounts
      // For now, returning null as this method is deprecated
      console.warn('getWalletDepositDetails is deprecated - Rail Operations no longer exists in V2');
      return null;
    } catch (usdtError) {
    }
  }
  async importWallet(walletInfo: any, endpoint: string): Promise<any> {

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': authService.getAuthorizationHeader(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(walletInfo.info), // Empty body; adjust if API requires specific data
      });

      const data = await response.json();
      return data;
      // if(data.address){
      //   if (walletInfo.currency === "BTC") {
      //     const btcUrl = `/bitcoin/wallet/${data.address}`;
      //     const btcDetails = await this.makeRequest<any>(btcUrl);
      //     const balance = btcDetails && btcDetails.balance ? String(btcDetails.balance) : '0';
      //     return {
      //       error: null,
      //       wallets: [{
      //         currency: "BTC",
      //         address: data.address,
      //         balance: balance
      //       }]
      //     }
      //   }
      //   if (walletInfo.currency === "ETH") {
      //     const ethUrl = `/ethereum/wallet/${data.address}`;
      //     const ethDetails = await this.makeRequest<any>(ethUrl);
      //     const ethbalance = ethDetails && ethDetails.balance ? String(ethDetails.balance) : '0';
      //     const cloneUrl = `/usdt/wallet/${data.address}/balance`;
      //     const erc20Details = await this.makeRequest<any>(cloneUrl);
      //     const erc20balance = erc20Details && erc20Details.balance ? String(erc20Details.balance) : '0';
      //     return {
      //        error: null,
      //       wallets: [{
      //         currency: "ETH",
      //         address: data.address,
      //         balance: ethbalance
      //       },
      //       {
      //         currency: "USDT_ERC20",
      //         address: data.address,
      //         balance: erc20balance
      //       }
      //       ]
      //     }
      //   } else if (walletInfo.currency === "TRX") {
      //     const trxUrl = `/tron/wallet/${data.address}`;
      //     const trxDetails = await this.makeRequest<any>(trxUrl);
      //     return {
      //        error: null,
      //       wallets: [{
      //         currency: "TRX",
      //         address: data.address,
      //         balance: trxDetails.trxBalance ? String(trxDetails.trxBalance) : "0"
      //       },
      //       {
      //         currency: "USDT_TRON",
      //         address: data.address,
      //         balance: trxDetails.usdtBalance ? String(trxDetails.usdtBalance) : "0"
      //       }
      //       ]
      //     }
      //   } else if (walletInfo.currency === "BNB") {
      //     const bnbUrl = `/bnb/wallet/${data.address}`;
      //     const bnbDetails = await this.makeRequest<any>(bnbUrl);
      //     const bep20Url = `/usdt-bep20/wallet/${data.address}/balance`;
      //     const bep20Details = await this.makeRequest<any>(bep20Url);
      //     return {
      //        error: null,
      //       wallets: [{
      //         currency: "BNB",
      //         address: data.address,
      //         balance: bnbDetails.trxBalance ? String(bnbDetails.trxBalance) : "0"
      //       },
      //       {
      //         currency: "USDT_BEP20",
      //         address: data.address,
      //         balance: bep20Details.usdtBalance ? String(bep20Details.usdtBalance) : "0"
      //       }
      //       ]
      //     }
      //   }
       
      // }
      //  return {
      //       wallets: [],
      //       error: data.message
      //     }
    } catch (error) {
      console.error('Error activating referrer:', error);
    }
  }
  // Método auxiliar para gestionar timeouts de forma más elegante
  private createTimeout(ms: number): { promise: Promise<never>; cancel: () => void } {
    let timeoutId: NodeJS.Timeout;
    const promise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`Request timeout after ${ms}ms`));
      }, ms);
    });

    return {
      promise,
      cancel: () => clearTimeout(timeoutId)
    };
  }
  
}

export const walletService = new WalletService();